/**
 * English comment normalized.
 * English comment normalized.
 */

import { resolveFloorPlanDrawRowUuid } from '../../internal/floor-plan-uuid';
import type { UppKonvaAreaSource } from '../../../model/upp-konva-source';
import { floorPlanFinitePositive } from '../../geometry/floor-plan-konva-sync';
import { UppFloorPlanModel } from '../floor-plan-model';
import {
  parseUppKonvaLabelFontSize,
  type UppFloorPlanPreferences,
} from '../floor-plan-preferences';
import type {
  FloorPlanAreaSourceDrawVertexItem,
  UppKonvaArea,
  UppKonvaAreaOrientation,
} from './floor-plan-wire-types';

export * from './floor-plan-wire-types';

import { vertexRowPolygon } from './shape-bridge/bridge-vertex';
import {
  adapterRowToShapeSource,
  shapeSourceToWireRow,
  wireRowToFloorPlanElement,
} from './shape-bridge/shape-bridge-registry';

/** Maps {@link UppKonvaArea.orientation} into presentation preferences. */
export function uppKonvaAreaOrientationToPreferences(
  orientation: UppKonvaAreaOrientation | undefined,
): Partial<UppFloorPlanPreferences> {
  /* PORTRAIT = no canvas rotation (0°); LANDSCAPE = 90° CCW so the room reads “sideways” in the frame. */
  switch (orientation ?? 'PORTRAIT') {
    case 'PORTRAIT':
      return { autoOptimizeOrientation: false, viewOrientationDeg: 0 };
    case 'LANDSCAPE':
      return { autoOptimizeOrientation: false, viewOrientationDeg: 90 };
    case 'AUTO':
      return { autoOptimizeOrientation: true };
    default:
      return { autoOptimizeOrientation: false, viewOrientationDeg: 0 };
  }
}

/** Inverse of {@link uppKonvaAreaOrientationToPreferences} for export when preferences are known. */
export function preferencesToUppKonvaAreaOrientation(p: UppFloorPlanPreferences): UppKonvaAreaOrientation {
  if (p.autoOptimizeOrientation) {
    return 'AUTO';
  }
  return p.viewOrientationDeg === 90 ? 'LANDSCAPE' : 'PORTRAIT';
}

/**
 * Campos opcionales de sala / host que influyen en {@link UppFloorPlanPreferences}.
 */
export interface UppKonvaAreaPresentationSlice {
  readonly orientation?: UppKonvaAreaOrientation | string;
  readonly viewZoom?: number;
  readonly marginPx?: number;
  readonly pixelRatioMax?: number;
  readonly labelSize?: string;
}

export function partialUppFloorPlanPreferencesFromPublicAreaSource(
  area: UppKonvaAreaPresentationSlice,
): Partial<UppFloorPlanPreferences> {
  const part: Partial<UppFloorPlanPreferences> = {};
  if (area.orientation !== undefined) {
    Object.assign(part, uppKonvaAreaOrientationToPreferences(area.orientation as UppKonvaAreaOrientation));
  }
  if (area.viewZoom !== undefined) {
    part.viewZoom = area.viewZoom;
  }
  if (area.marginPx !== undefined) {
    part.marginPx = area.marginPx;
  }
  if (area.pixelRatioMax !== undefined) {
    part.pixelRatioMax = area.pixelRatioMax;
  }
  if (area.labelSize !== undefined) {
    part.labelSize = parseUppKonvaLabelFontSize(area.labelSize);
  }
  return part;
}

/** Delega en {@link partialUppFloorPlanPreferencesFromPublicAreaSource} (argumento wire {@link UppKonvaArea}). */
export function partialUppFloorPlanPreferencesFromUppKonvaArea(
  area: UppKonvaArea,
): Partial<UppFloorPlanPreferences> {
  return partialUppFloorPlanPreferencesFromPublicAreaSource(area);
}

/**
 * Quita **`onGrouped`** del snapshot en memoria.
 */
export function uppKonvaAreaWire(area: UppKonvaArea & { onGrouped?: unknown }): UppKonvaArea;
export function uppKonvaAreaWire(area: UppKonvaAreaSource & { onGrouped?: unknown }): UppKonvaAreaSource;
export function uppKonvaAreaWire(
  area: (UppKonvaArea | UppKonvaAreaSource) & { onGrouped?: unknown },
): UppKonvaArea | UppKonvaAreaSource {
  const { onGrouped: _drop, ...rest } = area;
  void _drop;
  return rest as UppKonvaArea | UppKonvaAreaSource;
}

/**
 * @internal Domain aggregate — not exported from the package entry point.
 * English comment normalized.
 */
export function floorPlanModelFromAreaSource(src: UppKonvaArea): UppFloorPlanModel {
  const raw = [...src.shapes];
  const verts = raw.filter((r): r is FloorPlanAreaSourceDrawVertexItem => r.type === 'VERTEX');
  const nonVerts = raw.filter((r) => r.type !== 'VERTEX');
  verts.sort((a, b) => {
    const ap = vertexRowPolygon(a);
    const bp = vertexRowPolygon(b);
    if (ap !== bp) {
      return ap.localeCompare(bp);
    }
    return a.sort - b.sort;
  });
  const elements = [
    ...verts.map((v) =>
      wireRowToFloorPlanElement(v, resolveFloorPlanDrawRowUuid(v as unknown as Record<string, unknown>)),
    ),
    ...nonVerts.map((r) =>
      wireRowToFloorPlanElement(r, resolveFloorPlanDrawRowUuid(r as unknown as Record<string, unknown>)),
    ),
  ];
  return new UppFloorPlanModel(
    {
      widthCm: floorPlanFinitePositive(src.width, 1),
      heightCm: floorPlanFinitePositive(src.height, 1),
      name: src.name,
    },
    elements,
  );
}

/**
 * English comment normalized.
 */
export function areaSourceToAdapterArea(source: UppKonvaAreaSource): UppKonvaArea {
  const shapes = source.shapes.map(shapeSourceToWireRow);
  const base: UppKonvaArea = {
    width: source.width,
    height: source.height,
    ...(source.name !== undefined ? { name: source.name } : {}),
    shapes,
  };
  const o = source.orientation;
  if (o === 'PORTRAIT' || o === 'LANDSCAPE' || o === 'AUTO') {
    (base as UppKonvaArea & { orientation?: string }).orientation = o;
  }
  const m = source.mode;
  if (m === 'VERTEX' || m === 'SHAPES') {
    (base as UppKonvaArea & { mode?: string }).mode = m;
  }
  if (source.labelSize !== undefined && source.labelSize !== '') {
    (base as UppKonvaArea & { labelSize?: string }).labelSize = source.labelSize;
  }
  if (source.viewZoom !== undefined) {
    (base as UppKonvaArea & { viewZoom?: number }).viewZoom = source.viewZoom;
  }
  if (source.marginPx !== undefined) {
    (base as UppKonvaArea & { marginPx?: number }).marginPx = source.marginPx;
  }
  if (source.pixelRatioMax !== undefined) {
    (base as UppKonvaArea & { pixelRatioMax?: number }).pixelRatioMax = source.pixelRatioMax;
  }
  return base;
}

/**
 * English comment normalized.
 */
export function adapterAreaToAreaSource(wire: UppKonvaArea): UppKonvaAreaSource {
  return {
    width: wire.width,
    height: wire.height,
    ...(wire.name !== undefined ? { name: wire.name } : {}),
    ...(wire.orientation !== undefined ? { orientation: wire.orientation } : {}),
    ...(wire.mode !== undefined ? { mode: wire.mode } : {}),
    ...(wire.labelSize !== undefined && wire.labelSize !== '' ? { labelSize: wire.labelSize } : {}),
    ...(wire.viewZoom !== undefined ? { viewZoom: wire.viewZoom } : {}),
    ...(wire.marginPx !== undefined ? { marginPx: wire.marginPx } : {}),
    ...(wire.pixelRatioMax !== undefined ? { pixelRatioMax: wire.pixelRatioMax } : {}),
    shapes: wire.shapes.map(adapterRowToShapeSource),
  };
}

/**
 * English comment normalized.
 */
export function floorPlanModelFromPublicAreaSource(source: UppKonvaAreaSource): UppFloorPlanModel {
  return floorPlanModelFromAreaSource(areaSourceToAdapterArea(source));
}

export type { FloorPlanShapeBridge, SourceToWireInput } from './shape-bridge/shape-bridge-types';
export { adapterRowToShapeSource, registerFloorPlanShapeBridge } from './shape-bridge/shape-bridge-registry';
